import { SubscriptionPlanDropdown } from '../../components/SubscriptionPlanDropdown';
import { TextField } from '@/shared/components/TextField';
import CreateLayout from '@/shared/layout/createLayout';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePickerField } from '@/shared/components/DatePickerField';
import { useCreateOffer } from '../../hooks';
import { OfferFormData, OfferFormErrors } from '../../types/offerFormTypes';
import { ErrorIcon } from '@/shared/components/TextField/TextField';

interface CreateOfferFormProps {
  formData: OfferFormData;
  setFormData: Dispatch<SetStateAction<OfferFormData>>;
  errors: OfferFormErrors;
  setErrors: Dispatch<SetStateAction<OfferFormErrors>>;
  selectedPlans: string[];
  setSelectedPlans: Dispatch<SetStateAction<string[]>>;
  startDate: Date | undefined;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  endDate: Date | undefined;
  setEndDate: Dispatch<SetStateAction<Date | undefined>>;
  isSubmitting: boolean;
  hasAvailablePlans: boolean | null;
  onHasItemsChange: (hasItems: boolean) => void;
  onNavigateToCreateSubscription: () => void;
}

const CreateOffer = () => {
  const navigate = useNavigate();
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    selectedPlans,
    setSelectedPlans,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  } = useCreateOffer();

  const [hasAvailablePlans, setHasAvailablePlans] = useState<boolean | null>(
    null
  );

  const CreateSubscriptionHeader = () => {
    return <p className="text-titleLarge text-neutral-10 ">Utwórz ofertę</p>;
  };

  const CreateOfferForm = useMemo(
    () =>
      function CreateOfferForm({
        formData,
        setFormData,
        errors,
        setErrors,
        selectedPlans,
        setSelectedPlans,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        isSubmitting,
        hasAvailablePlans,
        onHasItemsChange,
        onNavigateToCreateSubscription,
      }: CreateOfferFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Szczegóły oferty
              </p>
              <div className="flex flex-col gap-8 w-[60%] ">
                <TextField
                  type="text"
                  inputMode="text"
                  label="Imię i nazwisko"
                  maxLength={200}
                  autoFocus={true}
                  value={formData.offerName}
                  onChange={(e) => {
                    if (errors.offerName) {
                      setErrors((prev: OfferFormErrors) => ({
                        ...prev,
                        offerName: '',
                      }));
                    }
                    const next = e.target.value.trimStart().slice(0, 200);
                    setFormData((prev: OfferFormData) => ({
                      ...prev,
                      offerName: next,
                    }));
                  }}
                  error={!!errors.offerName}
                  errorMessage={errors.offerName ?? ''}
                  disabled={isSubmitting}
                  fullWidth={true}
                />
                <div className="flex flex-col gap-2">
                  <SubscriptionPlanDropdown
                    toolTip={false}
                    maxVisibleItems={7}
                    selectedIds={selectedPlans}
                    onSelectionChange={(ids) => {
                      setSelectedPlans(ids);
                      if (errors.selectedPlans) {
                        setErrors((prev: OfferFormErrors) => ({
                          ...prev,
                          selectedPlans: '',
                        }));
                      }
                    }}
                    label={
                      <>
                        Plan subskrypcji{' '}
                        <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
                      </>
                    }
                    disabled={isSubmitting}
                    error={!!errors.selectedPlans}
                    errorMessage={errors.selectedPlans ?? ''}
                    onHasItems={onHasItemsChange}
                  />
                  {hasAvailablePlans === false && (
                    <p className="text-bodySmall align-middle text-on-surface-variant">
                      No subscription plans available.{' '}
                      <span
                        className="text-titleSmall underline text-[#2A4455] cursor-pointer"
                        onClick={onNavigateToCreateSubscription}
                      >
                        Utwórz plan
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Konfiguracja oferty
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 w-[60%]">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Typ rabatu"
                    maxLength={200}
                    width={'50%'}
                    value={'Procentowy'}
                    onChange={(e) => {
                      if (errors.offerType) {
                        setErrors((prev: OfferFormErrors) => ({
                          ...prev,
                          offerType: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev: OfferFormData) => ({
                        ...prev,
                        offerType: next,
                      }));
                    }}
                    error={!!errors.offerType}
                    errorMessage={errors.offerType ?? ''}
                    disabled={isSubmitting}
                    readOnly={true}
                  />
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Wartość rabatu"
                    maxLength={200}
                    width={'50%'}
                    value={formData.discountValue}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow only digits and one dot
                      value = value.replace(/[^0-9.]/g, '');

                      // Prevent multiple dots
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        return;
                      }

                      // Limit leading zeros to max 4 (before decimal)
                      if (
                        parts[0] &&
                        parts[0].length > 4 &&
                        parts[0].startsWith('0')
                      ) {
                        return;
                      }

                      // Limit decimals to 2 places
                      if (parts[1] && parts[1].length > 2) {
                        return;
                      }

                      // Convert to number for range check
                      const numericValue = Number(value);

                      // Allow empty input while typing
                      if (value === '') {
                        setFormData((prev) => ({ ...prev, discountValue: '' }));
                        return;
                      }

                      // Restrict range 0–100
                      if (numericValue < 0 || numericValue > 100) {
                        return;
                      }

                      if (errors.discountValue) {
                        setErrors((prev: OfferFormErrors) => ({
                          ...prev,
                          discountValue: '',
                        }));
                      }

                      setFormData((prev: OfferFormData) => ({
                        ...prev,
                        discountValue: value,
                      }));
                    }}
                    error={!!errors.discountValue}
                    errorMessage={errors.discountValue ?? ''}
                    disabled={isSubmitting}
                    trailingIcon={
                      <div>
                        {errors.discountValue && (
                          <ErrorIcon className="w-6 h-6 mr-5" />
                        )}
                        <div
                          className="absolute top-0 bottom-0 right-0 flex items-center justify-center 
                                            border-l border-[#474A4C] px-4 rounded-r-lg text-[#181A1B] text-bodyLarge py-1 "
                        >
                          %
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres obowiązywania
              </p>
              <div className="flex flex-col gap-8 w-[30%]">
                <DatePickerField
                  mode="range"
                  startDate={startDate}
                  endDate={endDate}
                  onRangeChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                    if (errors.startDate || errors.endDate) {
                      setErrors((prev: OfferFormErrors) => ({
                        ...prev,
                        startDate: '',
                        endDate: '',
                      }));
                    }
                  }}
                  label="Zakres dat"
                  endYear={2100}
                  error={!!errors.startDate || !!errors.endDate}
                  errorMessage={errors.startDate || errors.endDate || ''}
                  minDate={new Date()}
                  placement="top-end"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </>
        );
      },
    []
  );

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={CreateSubscriptionHeader}
      submitButtonLabel="Utwórz ofertę"
      submitButtonLoadingLabel="Tworzenie oferty..."
    >
      <CreateOfferForm
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        selectedPlans={selectedPlans}
        setSelectedPlans={setSelectedPlans}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        isSubmitting={isSubmitting}
        hasAvailablePlans={hasAvailablePlans}
        onHasItemsChange={setHasAvailablePlans}
        onNavigateToCreateSubscription={() => navigate('/Plany/Utwórz-plan')}
      />
    </CreateLayout>
  );
};

export default CreateOffer;
